// Portfolio data — merged from Propte's portfolio.tsx (base) + resume.tsx (work/education).
// See ../scratch/portfolio.tsx (parent planning repo) for the Phase 0 merge notes.
// Decisions (Jacob, 2026-06-05): X handle = `underemployed`; brand email = jacob@slvr.mn;
//            v1 scope = EMBED live demos.
//
// REMAINING TODOs (Jacob to confirm):
//   - Bookkeeping card href (internal /general-ledger route)
//   - Meteora card URL (currently live at bespy-app.fly.dev/meteora; becomes /meteora once embedded)
//   - Solana Event Listener repo URL (verify github.com/bespy/solana-app is public)
//   - chat-sync repo URL (currently generic github.com/bespy)
//   - YouTube handle @benefitothers (verify it's the real channel)
//   - avatar /me.png + logos /atomic.png /unsw.png are not in /public yet (fall back to initials)
//   - contact.tel is blank (placeholder removed)

import { Icons } from '#app/components/icons.tsx'

export const DATA = {
	name: 'Jacob Silverman',
	initials: 'JS',
	url: 'https://slvr.mn',
	location: 'Tel Aviv, IL',
	locationLink: 'https://www.google.com/maps/place/tel+aviv',
	// The on-page hero line. Deliberately short and NOT a noun list: `summaryLead`
	// immediately below it already names the harnesses, pipelines and engines, and
	// repeating them here read as stammering (2026-08-04). This states who the work is
	// for; the About paragraph states what it is and why.
	//
	// 2026-08-10 — issue 43's adopted line, finally applied. It had been live on
	// LinkedIn and the GitHub profile since 2026-08-05 while this surface still ran the
	// previous one, so two of three surfaces disagreed with the third. Deviates from
	// issue 43 in one place: that draft said "tool-spend auditing for Claude Code",
	// which describes `agent-spend-finops` — an idea, not a thing. Fairway is a
	// concurrency guard, so the line says what it actually does.
	// The "Full-stack AI engineer" label is NOT dropped: it carries in the page title,
	// `metaDescription`, `summaryLead`, `summaryFooter` and the résumé header. The hero
	// names the work; those name the role.
	description:
		'I’m building infrastructure for agentic workflows: budget monitoring and guardrails for agents.',
	// `<meta name="description">` only — the Google / link-share snippet, never rendered
	// on the page. Split from `description` on 2026-08-04 so shortening the hero didn't
	// also strip the AI keywords a recruiter or crawler searches on.
	metaDescription:
		'Full-stack AI engineer — ten years building agentic harnesses, multi-modal LLM pipelines, and the deterministic engines they reason over.',
	// About section (rendered in about.tsx): `summaryLead` is always shown, the
	// paragraphs in `summaryMore` sit behind a "See more" toggle, and
	// `summaryFooter` (the open-to-roles line) stays visible below the toggle.
	// CONDENSED 2026-09-15 (Jacob): the bio was duplicating product-face copy (Ledger AI /
	// Ledger UI / Fairway named inline) that the project cards already carry. Collapsed the
	// five-paragraph arc into a single lead paragraph that keeps the personal WHY (2020
	// trading → own ledger for taxes → FTX loss-quant → Claude 3 in the loop → business)
	// and drops everything that repeats what the product cards say. The pre-condensation
	// text is archived at ../../../../old-bio.md (planning repo). `summaryMore` is empty
	// on purpose — the "See more" toggle disappears.
	summaryLead:
		'I started building in crypto in 2020, and when I had to do my taxes nothing could reconcile activity spread across many wallets and chains, so I built my own software for that (see my project Ledger AI). The FTX collapse in 2022 pushed me into local LLMs to structure unstructured evidence and quantify losses. When Claude 3 launched in 2024 I put a model in the loop and started building budget monitoring tools and guardrails for agents (see my project Fairway). I also do freelance work building software in regulated industries (see Verify My Order).',
	summaryMore: [],
	summaryFooter:
		// AI leads (2026-08-06, Jacob). The trailing "including crypto" is gone: it read as a
		// caution flag to regulated employers and it put full-stack ahead of AI. The crypto
		// signal is not lost — the work history and `solana-app` still carry it.
		'_Open to AI and full-stack engineering roles — remote, or based in Tel Aviv._',
	avatarUrl: '/me.jpg', // square portrait in /public
	// Ordered by theme (AI/LLM → languages → web → data → crypto → fintech → infra).
	// Reorganized 2026-08-03 for the AI Engineer pivot: the AI/LLM block leads (it was
	// mid-list behind languages, so a recruiter screening for AI Engineer hit
	// "TypeScript, Python, Rust" first), and the block carries the vocabulary those JDs
	// actually screen on — RAG, tool calling, prompt/context engineering, MCP, inference
	// optimization. Every chip here must survive an interview question; don't add
	// aspirational ones. Prior pass (2026-07-02) was against the Fireblocks/Nimble/
	// BrightData JDs.
	skills: [
		// AI/LLM
		'Agentic AI / LLM tools',
		'RAG / semantic search',
		'Tool calling / function calling',
		'Prompt & context engineering',
		'Evals / structured output',
		'Local LLMs (Llama, MLX)',
		'Multi-modal ML (Whisper, Qwen3-VL, OCR)',
		'Inference optimization (quantization, batching)',
		'MCP',
		// languages
		'TypeScript',
		'Python',
		'Rust',
		// web
		'React',
		'Node.js',
		'Remix / React Router',
		'Next.js',
		'Data viz / charting (D3.js, SVG)',
		'Recharts',
		'Tailwind',
		'Radix / shadcn',
		'Zod',
		// data
		'PostgreSQL',
		'Redis',
		'SQLite / FTS5',
		'ORMs (Prisma, Drizzle)',
		// crypto
		'Solana (Anchor, Helius)',
		'On-chain forensics',
		'Applied cryptography (HMAC, ed25519, OAuth2)',
		// fintech
		'Xero & Stripe APIs',
		// infra
		'Inngest (durable workflows)',
		'Docker',
		'Kubernetes / Helm',
		'Terraform',
		'CI/CD (GitHub Actions)',
		'Testing (Vitest, Playwright)',
		'Sentry',
		'GCP',
		'AWS',
		'Fly.io',
	],
	contact: {
		email: 'jacob@slvr.mn',
		tel: '',
		social: {
			GitHub: {
				name: 'GitHub',
				url: 'https://github.com/bespy',
				icon: Icons.github,
				navbar: true,
			},
			LinkedIn: {
				name: 'LinkedIn',
				url: 'https://www.linkedin.com/in/jacob0x/',
				icon: Icons.linkedin,
				navbar: true,
			},
			X: {
				name: 'X',
				url: 'https://x.com/underemployed',
				icon: Icons.x,
				navbar: true,
			},
			Youtube: {
				name: 'Youtube',
				url: 'https://youtube.com/@benefitothers',
				icon: Icons.youtube,
				navbar: true,
			},
			email: {
				name: 'Send Email',
				url: 'mailto:jacob@slvr.mn',
				icon: Icons.email,
				navbar: false,
			},
		},
	},

	// Descending (newest first). Founder era broken into the dated projects that
	// appear in the Gallery; "Fiat & Crypto Bookkeeping Software" reserved for the
	// current 2026+ focus. Unbuilt stubs (delta-neutral simulator, P2E) are noted as in-progress,
	// not listed as standalone experience.
	work: [
		{
			company: 'Self-employed',
			// Was accountmd.com (brand retired 2026-07-25). The ↗ on a resume row is a
			// "show me the thing" affordance. Pointed at the general-ledger demo until
			// 2026-08-05, when the crypto demos were removed from the site (branch
			// archive/crypto-demos); now points at the build-log essay instead.
			href: '/writing/types-of-agentic-harness',
			badges: [],
			location: 'Remote',
			title: 'Software Engineer',
			logoUrl: '', // initials fallback
			start: '2020',
			end: 'Present',
			// Inventory, not narrative — see the note above `summaryMore`. The story beats
			// (FTX, Claude 3, the service guardrails) live in the bio; this entry carries
			// what only it has: scale figures, stack, and named client segments.
			//
			// TRIMMED 2026-08-05 (190 -> ~125 words). The 2026-08-04 split cut the ~70-word
			// duplicated service passage but left four phrases still verbatim in the bio:
			// "deterministic double-entry ledger ... crypto and fiat together", "a multi-modal
			// pipeline", the opening "built ... then productised it for clients" narrative,
			// and the closing "that stack now runs as a done-for-you ... service". All four
			// are gone; the bio owns every one of them. What is left is labelled inventory
			// (Ingestion / Analytics / Bookkeeping / Forensics / Fiat) and nothing else.
			// If you are adding a sentence here, check it is not already in `summaryMore`.
			//
			// ADDED 2026-08-06 — the `Compliance:` clause. The regulated-sector engagement
			// and the discovery work with non-engineers were claimed on no surface at all,
			// while being a preferred qualification on the roles being targeted (issue 47).
			// It sits here and not in the résumé `SUMMARY` because it is a fact, not a story
			// beat, and `SUMMARY` is chronological — see the narrative/inventory split above.
			// Keep it at this altitude. The engagement's specific approval mechanism must NOT
			// be described on any public surface (Jacob, 2026-08-06) — the combination of
			// domain + mechanism narrows the client to a few candidates. "Legally binding
			// deadlines" is the ceiling; see issue 29 for the fingerprint analysis.
			// CEILING RELAXED 2026-09-15 (Jacob): the fingerprint constraint is lifted for
			// this engagement. The product ("Verify My Order"), the mechanism ("contact-lens
			// prescription verification") and the retailer domain (dimplecolor.com) are now
			// on-surface here and on LinkedIn. Issue 29's analysis remains as history.
			// REWRITTEN 2026-08-10 (Jacob): business-first, the three-project block Jacob
			// approved (drafted for LinkedIn v3, adopted verbatim here — the two surfaces
			// intentionally match). The old labelled inventory
			// (Ingestion/Analytics/Bookkeeping/Forensics/Compliance/Fiat) moved to
			// ../../../drafts/six-jobs-one-engine.mdx (planning repo). ⚠️ The Compliance and
			// Fiat clauses now exist on NO public surface until that draft ships (Compliance
			// was added 2026-08-06 for issue 47; its mechanism-disclosure ceiling above still
			// applies if it returns).
			description:
				'Verify My Order — medical device prescription verification for a US retailer, built end-to-end as sole engineer. A companion Shopify app: orders don’t ship until a prescription is verified — a state machine that negotiates between doctors, patients and the retailer over multi-channel communications. Regulated-sector approval engine with legally binding response deadlines, scoped directly with the non-engineers who own the process. ~50 orders/day in production. https://verifymyorder.com · https://dimplecolor.com\n\nPrivate REIT — three-statement financial model built end-to-end as sole engineer from general-ledger and budget data. Inherited a complex VLOOKUP-reliant Excel workbook; iterated across 4+ tech stacks to find the right fit: (1) Python + xlwings, (2) Google Apps Script, (3) MS Access + SQL Server, (4) MS Excel with Power Tools. The MS stack landed as the answer. The technology was the easy part; the hard part was change management — getting finance users comfortable with dates as a time-series column rather than a crosstab, the shape that enables slicers and that the Power Pivot columnar engine needs. Power Query replaced the VLOOKUP chain, feeding a star-schema data model; DAX composed the three statements; CUBE functions pivoted them back into the P&L and balance-sheet layouts users expected; Power BI on top.\n\nXero/MYOB sync utility (same client) — custom OAuth2 app and Basiq (AU bank-feed API) integrations on Terraform-managed AWS infrastructure (ECR, Lambda, S3, Secrets Manager, CloudWatch, Route53), with D3.js across the reporting surface for interactive visualisations over financial and operational data. Supplied the general-ledger and bank feed data that fed the REIT model.',
		},
		{
			company: 'STRV',
			href: 'https://www.strv.com',
			badges: [],
			location: 'New York',
			title: 'Sales Engineer',
			logoUrl: '', // initials fallback
			start: '2017',
			end: '2018',
			description:
				'Sales engineering at a US software-development agency (New York) — scoping custom software builds and translating client requirements into project specs.',
		},
		{
			company: 'Atlassian',
			href: 'https://www.atlassian.com',
			badges: [],
			location: 'Sydney',
			title: 'Software Engineer Intern',
			logoUrl: '', // initials fallback
			start: '2014',
			end: '2015',
			description:
				'Software engineering internship in the Jira/Confluence ecosystem, during my UNSW engineering degree.',
		},
	],
	education: [
		{
			school: 'University of New South Wales',
			href: 'https://www.unsw.edu.au/study/undergraduate/bachelor-of-engineering-honours-electrical',
			degree: "Bachelor's Degree in Engineering (Electrical)",
			logoUrl: '', // no asset yet → initials fallback (set to '/unsw.png' once added to /public)
			start: '2012',
			end: '2016',
			description:
				'Electrical engineering — signals, control systems, and embedded design. Where the systems thinking started.',
		},
	],

	// Projects (2026-08-04): the site has exactly two buckets, split on one test — does the
	// thing have a buy link? If yes it is a Project and it gets a card here. If no it is a
	// Writing, and it goes in the flat Writing list alongside the essays (see `demos` below).
	// This replaced the old "Gallery" section, which mixed the two.
	//
	// ⚠️ NOTHING UNSHIPPED GOES IN THIS ARRAY (rule set 2026-08-04, Jacob). The section
	// previously carried three cards, two of which rendered the word "PLACEHOLDER" and a
	// "draft — not yet launched" eyebrow to visitors. A build-status eyebrow is a dev note
	// on a public page, so "render it as a draft" is not a fix for it — the fix is not
	// rendering it. Unshipped products are kept as the commented scaffolds below and are
	// re-added, with real copy, on the day they have a URL that resolves.
	//
	// SHIPPED 2026-08-05 — LEDGER UI is now the card above, per this section's own rule
	// ("re-added, with real copy, on the day they have a URL that resolves"). It resolves:
	// https://ledgerui.fly.dev/ returns 200 and serves the component docs.
	//   ⚠️ The card points at the FLY URL, not `ledgerui.com` — the domain is not wired yet.
	//   Repoint it the day it is, and keep the naming decision intact: `accountui.com` is
	//   rejected twice over (taken since 2026-06-18, and "account UI" names account
	//   settings/profile/billing in front-end vocabulary — the category this kit is not).
	//   Decision: rr-folio issue 41; record: ~/code/ideas/ideas-store/accounting-motion-kit.md.
	//   Copy constraints still apply: keep "ledger" in the ACCOUNTING-REGISTER sense and
	//   avoid wallet/custody language (Ledger SAS owns ledger.com); do not talk up revenue —
	//   this is a reputation asset, not a revenue line; do not call it a grid, it composes
	//   over a headless engine.
	//
	// PENDING — the PROPERTY product (`propte.com`). Was the `PropertyMap` placeholder.
	// After the GCP→Fly cutover, propte.com stops fronting the ledger app and points here
	// (Jacob, 2026-08-04). ⚠️ This SUPERSEDES fundte issue 09, which still says
	// propte.com → slvr.mn (this folio). It does not — nothing here needs a domain added to
	// the slvr-mn Fly cert. The `propte` slug was deliberately kept free for this product;
	// see ~/github/propte/README.md on why the archive was named `fundte-archive`.
	//   Blocked on: the product does not exist yet.
	//
	// SHIPPED 2026-08-05 — FAIRWAY is now the card above. github.com/bespy/fairway is a
	// PUBLIC LANDING REPO ONLY (README, ~4.7KB) and that is deliberate — the card links there,
	// and the code is NOT pushed to it. Do not push app-tracer there without asking.
	//   The planning parent ~/code/fairway stays local-only: it holds the signal sprint, the
	//   venture map and the competitive scan. Verified 2026-08-05 that the public repo has no
	//   issues/ directory, so none of that is exposed — that separation is the whole point of
	//   the nested-repo layout and must survive any future publish.
	//   Deliverable lives at ~/code/fairway/apps/app-fairway (Go, own git repo). The public repo
	//   carries the full README (14KB, pushed 2026-08-16) — nine tools, not just the hook — so
	//   folio copy may describe the whole suite; the code itself is still not pushed.
	projects: [
		{
			// (MIT) is future-state-but-decided: apps/app-fairway/LICENSE is MIT on disk and
			// goreleaser ships it; fairway issue 20 locked the engine open-source (fallback
			// there: withhold the rules catalogue, never the engine — so this label is stable).
			title: 'Fairway (MIT): devtools for Claude Code',
			// Commands named at Jacob's call (2026-08-16), and the list deliberately includes
			// one that is not built yet so the copy does not need redoing when it lands:
			// `/insights` (gaps, friction, insights), `claude agents` (agents) and the
			// PreToolUse/PostToolUse hooks all ship today; `/usage` is fw-35, planned.
			eyebrow: 'Extends /insights, /usage, claude agents and hooks',
			href: 'https://github.com/bespy/fairway',
			dates: '2026 — Present',
			active: true,
			featured: true,
			description:
				'Guardrails and devtools for collaborating agents. Claude Code’s docs tell you what to do when you ask; Fairway tells you what to do when you don’t know what to ask, and gives you hooks to encourage patterns and discourage antipatterns. For example, a concurrency guard that intercepts wide git operations (`reset --hard`, `checkout .`, force push) before one autonomous coding agent clobbers another’s uncommitted work — multi-agent-safe by design, no shared state between sessions, pre-run interception at the hook layer.\n\nNine small tools sharing one Go binary, one WAL-mode SQLite store and one MCP server, so anything one tool writes another can read — each extending commands Claude Code already ships (subset: /insights, /usage). Pick the ones you want: most are read-only pull commands that need nothing installed, and two wire into your setup with explicit install and uninstall verbs. The binary alone creates no files, opens no ports, writes no config. Next: budget management for agents, built on the reconciliation primitives from Ledger AI.',
			resumeBlurb:
				'Guardrails and devtools for collaborating coding agents. Nine small tools sharing one Go binary, one WAL-mode SQLite store and one MCP server — each extending a command Claude Code already ships. The guard rules intercept wide git operations (`reset --hard`, `checkout .`, force push) at the hook layer before one agent clobbers another’s uncommitted work. Pre-run interception, no shared state between sessions.',
			technologies: [
				'Go',
				'Claude Code hooks',
				'MCP',
				'JSON-RPC 2.0 over HTTP + SSE',
				'SQLite (WAL)',
				'Git plumbing',
			],
			links: [
				{
					type: 'GitHub',
					href: 'https://github.com/bespy/fairway',
					icon: <Icons.globe className="size-3" />,
				},
			],
			video: '',
		},
		{
			// BRAND RESTORED TO THE TITLE (Jacob, 2026-08-10), reversing the 2026-08-04
			// "functional, not a brand" decision. That decision rested on one thing: a
			// reader clicks a product name, and the name then landed on a stale build of
			// the retired product. It no longer does — the card points at ledgerai.fly.dev,
			// verified 200. With a destination that resolves, the name is an asset rather
			// than a trap.
			// Spelling is `Ledger AI`, spaced, to match its sibling `Ledger UI` — the
			// established form (21 uses across the store vs 4 closed-up). The lowercase
			// domain does not decide the display name: `ledgerui.com` renders as "Ledger
			// UI", so `ledgerai.fly.dev` does not force "LedgerAI". Issue records keep
			// whatever they were written with; this is the user-facing spelling. "Financial
			// reporting" stays the head noun and crypto sits in the parenthetical, so the
			// work is not pigeonholed as crypto-only; that half of the 2026-08-04 reasoning
			// still holds. Same move the resume row made at line ~168.
			title: 'Ledger AI: forensic crypto and DeFi tax bookkeeping',
			// ⚠️ DESTINATION NOT LIVE YET. accountmd.com still resolves to the GCP LB
			// (34.8.148.170) serving a STALE build of the retired invite-only
			// "deterministic books" product (commit 851a9bf; that line is archived, no
			// further deploys — see ~/github/propte/README.md). The funnel lives in
			// ~/code/ledgerai/apps/app-bench; `/wallet-check` 404s today.
			//
			// ROUTE SHAPE (per the app-bench window, 2026-08-04): the `/crypto-tax` service
			// landing page is being promoted to the site ROOT `/`, and `/wallet-check` stays
			// its own route. So this card links to the apex, not to `/crypto-tax`. That move
			// was not yet on disk in app-bench when this was written (its `_marketing+/` was
			// clean at 3af1f84 with the old homepage still at `index.tsx`) — if it gets
			// reversed, this href goes back to `https://accountmd.com/crypto-tax`.
			//
			// THE UNBLOCKING STEP IS THE DNS CUTOVER, NOT THE FLY DEPLOY. fundte issue 58's
			// runbook: (1) deploy app-bench to Fly, (2) reduced secrets, (3) test on
			// .fly.dev, (4) DNS cutover accountmd.com, (5) tear down GCP. These URLs start
			// working at (4) — after (1) the funnel is live only on a .fly.dev hostname
			// while accountmd.com still serves the stale build. No repoint needed when it
			// lands: fundte issue 09 makes accountmd.com the product's canonical domain.
			// NOTE: this used to read "the do-not-deploy gate above blocks the whole section
			// anyway" — that gate was removed on 2026-08-04 with the placeholder cards, so
			// nothing blocks this section now. This card ships with a live href whose
			// destination is not yet serving the product it describes; that is the open risk.
			//
			// Copy constraints inherited from the source pages (fundte issue 64) — these are
			// load-bearing, not stylistic:
			//   1. SCOPE BOUNDARY — workpapers only; the client's own accountant reviews and
			//      files. Never imply filing, lodgement or tax advice.
			//   2. NEVER MARKET RECOVERY — losses are documented to substantiate a capital
			//      loss, never chased. Regulator scam-taint.
			//   3. Read-only, public address only — no wallet connect, no signature.
			//   4. NEVER SAY "FREE" (2026-08-04, Jacob, twice in one session). First the
			//      card claimed a "free report" — wrong, the report was paid. Then it
			//      claimed a "free preview" — also wrong, the preview is NOT BUILT and was
			//      removed rather than shipped. There is no free tier. The offering is a
			//      done-for-you HIGH-VOLUME crypto + fiat reporting SERVICE. Do not write
			//      a self-serve promise ("paste an address, get a report") either — that
			//      describes the unbuilt preview, not what is sold.
			eyebrow: 'Done-for-you · the messy tail worked by hand',
			// Repointed 2026-08-05 (Jacob) from accountmd.com, which still serves a
			// stale build of the retired product. ⚠️ ledgerai.fly.dev does not resolve
			// yet — wired ahead of its deploy, deliberately, same as the URLs below.
			href: 'https://ledgerai.fly.dev',
			dates: '2026 — Present',
			active: true,
			featured: true,
			description:
				'A multi-tenant TypeScript service that books crypto and fiat into a double-entry ledger. On-chain data flows through Inngest-orchestrated Solana blockchain pipelines (event listening, transaction parsing); off-chain data arrives via Xero/MYOB sync (durable, retry-safe, per-tenant concurrency) and a local multi-modal agent harness over bank statements, screenshots and CSVs for the messy sources. $1M+ reconciled last financial year across multiple on-chain and off-chain venues — hundreds of thousands of events, tens of thousands of fiat transactions. The same substrate also feeds on-chain forensics — wallet-flow reconstruction across chains for compliance and loss-quantification.\n\nBackend: React Router v7 + TypeScript server on GCP Cloud Run with PostgreSQL (Cloud SQL) and Prisma, ~120 Prisma tables across 18 schemas, GnuCash-shaped. Inngest orchestrates a multi-step pipeline that ingests Solana on-chain events (via RPC and Anchor). Multi-tenancy via Postgres RLS as authorization (not just filtering), fed by a Zanzibar-style access-grant table.\n\nFrontend: React components that feel familiar if you’re used to MS Excel, optimised for information density. SSR via React Router v7; per-report view state (filters, pagination, selection) in an HMAC-signed cookie with Zod schemas; TanStack Table with keyboard navigation; inline mutations with useFetcher; Framer Motion over Radix UI. Built as a headless engine for distribution.\n\nAgent Harness: Python pipeline for ingesting unstructured data from voice, vision and OCR using local models on MLX (Apple Silicon). Model choice per task via evals (vision-language models benchmarked for OCR on legible vs degraded documents). Structured output feeds downstream pipelines like Ledger AI.\n\nLineage: originated as a Python MVP in 2022 — a REST ingestion client over Postgres, reconciling exchange trades against on-chain transfers, extended with event-driven on-chain services — RPC listeners (web3.py) feeding async handlers, fronted by Flask/gunicorn, containerised and deployed to Kubernetes via Helm. Rebuilt in TypeScript as the schema stabilised and multi-tenant requirements landed.',
			resumeBlurb:
				'A multi-tenant TypeScript service that books crypto and fiat into a double-entry ledger. On-chain data via Inngest-orchestrated Solana blockchain pipelines; off-chain via Xero/MYOB sync (durable, retry-safe, per-tenant concurrency) and a Python/MLX agent harness for messy sources (bank statements, screenshots, CSVs). React Router v7, Postgres + Prisma (~120 tables, 18 schemas), multi-tenant via Postgres RLS as authorization. $1M+ reconciled last financial year across multiple on-chain and off-chain venues; hundreds of thousands of events. Same substrate feeds on-chain forensics. Rebuilt in TypeScript from a 2022 Python MVP as the schema stabilised.',
			technologies: [
				'TypeScript',
				'React Router',
				'Postgres',
				'Solana decoders',
				'FIFO cost basis',
			],
			links: [
				{
					// Was 'Try it' -> /wallet-check, the free-preview entry. That preview was
					// never built, so both the label and the destination were wrong: "Try
					// it" promises self-serve, and /wallet-check will not exist. Points at
					// the apex until the new site's routes are known.
					type: 'Learn more',
					href: 'https://ledgerai.fly.dev',
					icon: <Icons.globe className="size-3" />,
				},
			],
			video: '',
		},
	],

	// `demos` MOVED 2026-08-05. Each demo now carries its own metadata in
	// app/features/portfolio/demos/<name>/meta.tsx, and the list is globbed from those
	// files by demos/registry.ts. A demo used to be three things in three places —
	// component, route, and an entry in this array — so deleting the first two left it
	// listed on the home page anyway. Import LISTED_DEMOS / findDemo from the registry.
} as const
